import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Box component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconBox extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M4.77 4.05145C5.19 3.24145 6.03 2.73145 6.94 2.73145H11.14V7.48145H2.99L4.78 4.05145H4.77ZM1.11 7.83145C1.11 7.83145 1.13 7.79145 1.14 7.77145L3.44 3.35145C4.12 2.05145 5.47 1.23145 6.95 1.23145H16.92C18.41 1.23145 19.77 2.06145 20.44 3.39145L22.67 7.76145C22.67 7.76145 22.71 7.86145 22.73 7.91145C22.77 8.01145 22.8 8.11145 22.8 8.22145V20.7114C22.8 21.3214 22.56 21.9114 22.12 22.3414C21.69 22.7714 21.1 23.0214 20.49 23.0214H3.31C2.7 23.0214 2.11 22.7814 1.68 22.3414C1.25 21.9114 1 21.3214 1 20.7114V8.23145C1 8.08145 1.04 7.95145 1.11 7.83145V7.83145ZM20.83 7.48145L19.09 4.07145C18.67 3.25145 17.83 2.73145 16.91 2.73145H12.64V7.48145H20.83ZM14.23 17.6214C13.82 17.6214 13.48 17.9614 13.48 18.3714C13.48 18.7814 13.82 19.1214 14.23 19.1214H18.13C18.54 19.1214 18.88 18.7814 18.88 18.3714C18.88 17.9614 18.54 17.6214 18.13 17.6214H14.23ZM2.5 8.98145V20.7214C2.5 20.9314 2.59 21.1414 2.74 21.2914C2.89 21.4414 3.1 21.5314 3.31 21.5314H20.48C20.7 21.5314 20.9 21.4414 21.05 21.2914C21.2 21.1414 21.29 20.9314 21.29 20.7214V8.98145H2.5Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-box') === undefined) {
  customElements.define('cat-icon-box', BlocksIconBox);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-box': BlocksIconBox;
  }
}
